import React from 'react';

import { Button } from '../atoms/button/button';
import { useCarouselContext } from './carousel';
import ArrowRightIcon from '@tradera/blueprint/theme/icons2/arrow-right.svg?jsx';
import ArrowLeftIcon from '@tradera/blueprint/theme/icons2/arrow-left.svg?jsx';
import classnames from 'classnames';

import css from './carousel.module.scss';

/**
 * A default button for scroll
 */
const LeftArrowButton = ({ ...props }) => {
    const { light, size, ...rest } = props;

    return (
        <Button
            theme={light ? 'unbutton' : 'action'}
            className={css.arrowButton}
            aria-label="Scroll carousel backwards"
            {...rest}>
            <ArrowLeftIcon className={`icon icon-${size}`} />
        </Button>
    );
};

/**
 * A default button for scroll
 */
const RightArrowButton = ({ ...props }) => {
    const { light, size, ...rest } = props;

    return (
        <Button
            theme={light ? 'unbutton' : 'action'}
            className={css.arrowButton}
            aria-label="Scroll carousel forwards"
            {...rest}>
            <ArrowRightIcon className={`icon icon-${size}`} />
        </Button>
    );
};

type ButtonChildrenProps = {
    onClick: () => void;
    'aria-controls': string;
};

type ButtonProps = {
    children?: (args: ButtonChildrenProps) => React.ReactNode;
    size?: 'sm' | 'md';
    className?: string;
    light?: boolean;
    /**
     * Aligns the button vertically to the item cards.
     *
     * You can either use this or if it doesn't work for you, use the css variable  `--carousel-button-top-offset` to set the offset (usually through a css class that you pass to the button)
     */
    alignVerticallyToItemCards?: boolean;
};

/**
 * Button that scrolls the list to the left
 *
 * It defaults to a LeftArrowButton but can be overridden by providing children.
 * If using default buttons a size can be passed to it, default is "sm"
 *
 * Children are called with a function which passes `onClick`
 */
export const LeftButton = ({
    children,
    size = 'sm',
    light = false,
    className,
    alignVerticallyToItemCards,
    ...props
}: ButtonProps) => {
    const context = useCarouselContext();

    const {
        numberItemsToScroll,
        uniqueId,
        childrenMap,
        isFirstItemVisible,
        areAllItemsHidden,
        firstVisibleItemIndex,
        showScrollbar,
        rows
    } = context;

    // If no items have rendered, don't show the buttons
    if (areAllItemsHidden || isFirstItemVisible) return null;

    const handleOnClick = () => {
        const previousItem = childrenMap.get(
            Math.max(0, firstVisibleItemIndex - numberItemsToScroll * rows)
        );

        previousItem.ref?.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest'
        });
    };

    return (
        <div
            className={classnames([css.leftButton, className], {
                [css.md]: size === 'md',
                [css.light]: light,
                [css.alignVerticallyToItemCards]: alignVerticallyToItemCards,
                [css.showScrollbar]: showScrollbar
            })}
            {...props}>
            {/* KATS: fix styling for blueprint's span.btn round, then {children()} can be replaced by {children} */}
            {children ? (
                children({
                    onClick: handleOnClick,
                    'aria-controls': uniqueId?.current
                })
            ) : (
                <LeftArrowButton
                    size={size}
                    light={light}
                    onClick={handleOnClick}
                    aria-controls={uniqueId?.current}
                />
            )}
        </div>
    );
};

/**
 * Button that scrolls the list to the right
 *
 * It defaults to a RightArrowButton but can be overridden by providing children.
 * If using default buttons a size can be passed to it, default is "sm"
 *
 * Children are called with a function which passes `onClick`
 */

export const RightButton = ({
    children,
    size = 'sm',
    light = false,
    className,
    alignVerticallyToItemCards,
    ...props
}: ButtonProps) => {
    const context = useCarouselContext();

    const {
        uniqueId,
        childrenMap,
        numberItemsToScroll,
        isFirstItemVisible,
        isLastItemVisible,
        lastVisibleItemIndex,
        areAllItemsHidden,
        showScrollbar,
        rows
    } = context;

    // If no items have rendered, don't show the buttons
    if (areAllItemsHidden || isLastItemVisible) return null;

    const handleOnClick = () => {
        // get previous not-in-view item to scroll to
        const nextItem = childrenMap.get(
            Math.min(
                childrenMap.size - 1,
                lastVisibleItemIndex + numberItemsToScroll * rows
            )
        );

        nextItem.ref?.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest'
        });
    };

    return (
        <div
            className={classnames([css.rightButton, className], {
                [css.md]: size === 'md',
                [css.light]: light,
                [css.alignVerticallyToItemCards]: alignVerticallyToItemCards,
                [css.showScrollbar]:
                    showScrollbar && !(isFirstItemVisible && isLastItemVisible)
            })}
            {...props}>
            {children ? (
                children({
                    onClick: handleOnClick,
                    'aria-controls': uniqueId?.current
                })
            ) : (
                <RightArrowButton
                    size={size}
                    light={light}
                    onClick={handleOnClick}
                    aria-controls={uniqueId?.current}
                />
            )}
        </div>
    );
};
